import { useState } from 'react'
import { AlertTriangle, CheckCircle2, Eye, EyeOff, KeyRound, Mail, RefreshCw, X } from 'lucide-react'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { usePermissions } from '@/shared/hooks/usePermissions'
import {
  cambiarCorreoDeAcceso,
  restablecerContrasena,
} from '@/features/proveedores/api/accesoDelProveedor.service'
import {
  contrasenaGenerada,
  errorDeContrasena,
  errorDeCorreo,
  mensajeDeError,
  normalizarCorreo,
  reglasDeContrasena,
} from '@/features/proveedores/lib/accesoDelProveedor'

// CC-03 · SP-251. Restablecer la contraseña y cambiar el correo de acceso de un
// proveedor existente. No se muestra la contraseña actual: no existe forma de
// leerla, y la nueva solo se ve mientras el admin la escribe.

const PERMISO_EDITAR = 'proveedores.administracion.proveedores.editar'

type Accion = 'contrasena' | 'correo'

interface AccesoDelProveedorProps {
  supplierId: string
}

const AccesoDelProveedor = ({ supplierId }: AccesoDelProveedorProps) => {
  const { hasPermission } = usePermissions()
  const [accion, setAccion] = useState<Accion | null>(null)
  const [valor, setValor] = useState('')
  const [verContrasena, setVerContrasena] = useState(false)
  const [tocado, setTocado] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [errorDelBack, setErrorDelBack] = useState<string | null>(null)
  const [resultado, setResultado] = useState<string | null>(null)

  if (!hasPermission(PERMISO_EDITAR)) return null

  const abrir = (siguiente: Accion) => {
    setAccion(siguiente)
    setValor('')
    setVerContrasena(false)
    setTocado(false)
    setErrorDelBack(null)
    setResultado(null)
  }

  const cerrar = () => {
    if (enviando) return
    setAccion(null)
    setValor('')
    setErrorDelBack(null)
  }

  const errorDeValidacion =
    accion === 'contrasena'
      ? errorDeContrasena(valor)
      : accion === 'correo'
        ? errorDeCorreo(valor)
        : null

  const confirmar = async () => {
    setTocado(true)
    if (!accion || errorDeValidacion) return
    setEnviando(true)
    setErrorDelBack(null)
    try {
      if (accion === 'contrasena') {
        await restablecerContrasena(supplierId, valor)
        setResultado(
          'Contraseña restablecida. Se avisó al proveedor por correo y se cerraron sus sesiones abiertas. Compártele la contraseña nueva por un medio seguro: el correo no la incluye.',
        )
      } else {
        const correo = normalizarCorreo(valor)
        await cambiarCorreoDeAcceso(supplierId, correo)
        setResultado(
          `Correo de acceso cambiado a ${correo}. Se avisó al correo anterior y al nuevo, y se cerraron las sesiones abiertas del proveedor.`,
        )
      }
      setAccion(null)
      setValor('')
    } catch (errCrudo: unknown) {
      const err = errCrudo as { message?: string | string[] }
      setErrorDelBack(
        mensajeDeError(
          err,
          accion === 'contrasena'
            ? 'No se pudo restablecer la contraseña'
            : 'No se pudo cambiar el correo',
        ),
      )
    } finally {
      setEnviando(false)
    }
  }

  const reglas = reglasDeContrasena(valor)

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
      <div className="flex items-start gap-3">
        <KeyRound className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
        <div>
          <h3 className="text-base font-semibold text-gray-900">Acceso del proveedor</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Estas acciones se aplican al momento, sin pasar por Guardar. Al hacerlas se cierran
            las sesiones abiertas del proveedor y se le avisa por correo.
          </p>
        </div>
      </div>

      {resultado && (
        <div className="flex items-start gap-2 mt-4 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800">{resultado}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mt-4">
        <button onClick={() => abrir('contrasena')} className={estiloDeBoton('secundario')}>
          <KeyRound className="w-4 h-4" />
          Restablecer contraseña
        </button>
        <button onClick={() => abrir('correo')} className={estiloDeBoton('secundario')}>
          <Mail className="w-4 h-4" />
          Cambiar correo de acceso
        </button>
      </div>

      {accion && (
        <CapaDeModal zIndex={60}>
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <button
              onClick={cerrar}
              disabled={enviando}
              aria-label="Cerrar"
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>

            {accion === 'contrasena' ? (
              <>
                <h2 className="text-lg font-bold text-gray-900 pr-6">Restablecer contraseña</h2>
                <p className="text-sm text-gray-600 mt-2">
                  Escribe la contraseña nueva. El proveedor recibe un aviso por correo, pero el
                  correo no la lleva: tendrás que compartírsela tú.
                </p>

                <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">
                  Contraseña nueva <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type={verContrasena ? 'text' : 'password'}
                      value={valor}
                      onChange={(e) => setValor(e.target.value)}
                      autoComplete="new-password"
                      className="w-full pl-3 pr-10 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setVerContrasena((v) => !v)}
                      aria-label={verContrasena ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {verContrasena ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setValor(contrasenaGenerada())
                      setVerContrasena(true)
                    }}
                    className={estiloDeBoton('secundario')}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Generar
                  </button>
                </div>
                <ul className="mt-2 space-y-0.5 text-xs">
                  {[
                    [reglas.largo, 'Al menos 8 caracteres'],
                    [reglas.mayuscula, 'Una mayúscula'],
                    [reglas.numero, 'Un número'],
                  ].map(([cumple, texto]) => (
                    <li key={texto as string} className={cumple ? 'text-green-600' : 'text-gray-400'}>
                      {cumple ? '✓' : '•'} {texto as string}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <h2 className="text-lg font-bold text-gray-900 pr-6">Cambiar correo de acceso</h2>
                <p className="text-sm text-gray-600 mt-2">
                  Es el correo con el que el proveedor inicia sesión. Se avisa al correo anterior y
                  al nuevo. No cambia los correos de contacto del expediente (ventas, cuentas por
                  pagar, fiscal).
                </p>

                <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">
                  Correo nuevo <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                  autoComplete="off"
                  className="w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {tocado && errorDeValidacion && (
                  <p className="text-xs text-red-600 mt-1">{errorDeValidacion}</p>
                )}
              </>
            )}

            {accion === 'contrasena' && tocado && errorDeValidacion && (
              <p className="text-xs text-red-600 mt-2">{errorDeValidacion}</p>
            )}

            {errorDelBack && (
              <div className="flex items-start gap-2 mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{errorDelBack}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-5">
              <button onClick={cerrar} disabled={enviando} className={estiloDeBoton('secundario')}>
                Cancelar
              </button>
              <button onClick={confirmar} disabled={enviando} className={estiloDeBoton('primario')}>
                {enviando ? 'Guardando…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}
    </div>
  )
}

export default AccesoDelProveedor
